import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Study, StudyClient } from 'jf/api';
import { DevExPageLayout } from 'jf/common/DevExPageLayout';
import { useJFOverlay } from 'jf/common/useJFOverlay';
import { useTargetingClient } from 'jf/services/useTargetingClient';
import { useClientFlags } from 'jf/utils/useClientFlags';
import { useClientQuery } from 'jf/utils/useClientQuery';

import { makeStudyAnalyzerPath } from '../StudyAnalyzerPage';
import { TeamCascader } from '../TeamCascader';
import { useCanViewSurveyResults } from '../useCanViewSurveyResults';

import { ManageSurveyDrawer } from './ManageSurveyDrawer';
import { MessagingDisconnectedModal } from './MessagingDisconnectedModal';
import { ParticipationBySegmentCard } from './ParticipationBySegmentCard';
import { ParticipationCard } from './ParticipationCard';

export const ParticipationTab: React.FC = () => {
  const flags = useClientFlags();
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const { studyRef, teamRef } = useParams<{ studyRef: string; teamRef: string }>();
  const navigate = useNavigate();
  const messagingDisconnectedModal = useJFOverlay(MessagingDisconnectedModal);

  const { data: study } = useClientQuery(StudyClient.getStudy, { studyRef });

  const { useGetStatus, useGetStudyTargets } = useTargetingClient();
  const { data: messagingStatus } = useGetStatus();
  const { data: targets } = useGetStudyTargets({ studyRef, isEnabled: flags.slackTargeting });

  const canViewResults = useCanViewSurveyResults({ studyRef });

  useEffect(() => {
    // check if Slack is disconnected on a Slack-targeted survey
    if (
      study?.status === Study.status.OPEN &&
      targets?.length &&
      messagingStatus?.connected === false
    ) {
      messagingDisconnectedModal.open();
    }
  }, [study, targets, messagingStatus]);

  return (
    <DevExPageLayout>
      {canViewResults && (
        <TeamCascader
          studyRef={studyRef}
          value={teamRef}
          onChange={(teamRef) =>
            navigate(makeStudyAnalyzerPath(studyRef, teamRef, 'participation'))
          }
          allowClear
        />
      )}
      <ManageSurveyDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        studyRef={studyRef!}
      />
      <ParticipationCard
        studyRef={studyRef}
        teamRef={teamRef}
        openManageDrawer={() => setDrawerOpen(true)}
      />
      {study && <ParticipationBySegmentCard studyRef={studyRef} teamRef={teamRef} />}
    </DevExPageLayout>
  );
};
